import { isoCountryCodes } from "./lib/countries";
import { scalar, type Answers } from "./lib/form-model";

type PassportExtract = {
  full_name?: string | null;
  passport_number?: string | null;
  date_of_birth?: string | null;
  sex?: string | null;
  country_of_birth?: string | null;
  place_of_birth?: string | null;
  expiry_date?: string | null;
  issuing_country?: string | null;
  nationality?: string | null;
};

type SavedPassport = {
  given_names?: string | null;
  middle_names?: string | null;
  family_name?: string | null;
  second_family_name?: string | null;
  birth_date?: string | null;
  birth_city?: string | null;
  birth_country?: string | null;
  nationalities?: string[] | null;
  sex?: string | null;
  passport_number?: string | null;
  issuing_country?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
};

/** Passport status as the Passport Center form models it (camelCase, empty strings when unset). */
export type PassportStatusAnswers = {
  reason: string;
  applicationStatus?: string;
  expectedDate: string;
  dateUnknown: boolean;
  applicationDate?: string;
  applicationDateIsApproximate?: boolean;
  expedited?: string;
};

type SavedPassportStatus = {
  reason?: string | null;
  application_status?: string | null;
  application_date?: string | null;
  application_date_is_approximate?: boolean | null;
  expedited?: string | null;
  expected_date?: string | null;
  date_unknown?: boolean | null;
};

export function passportStatusPayload(value: PassportStatusAnswers) {
  return {
    reason: value.reason || "",
    application_status: value.applicationStatus || null,
    application_date: value.applicationDate || null,
    application_date_is_approximate: value.applicationDateIsApproximate ?? null,
    expedited: value.expedited || null,
    expected_date: value.expectedDate || null,
    date_unknown: Boolean(value.dateUnknown),
  };
}

/** Returns ``null`` when no passport status is stored. */
export function passportStatusFromSaved(data: SavedPassportStatus | null | undefined): PassportStatusAnswers | null {
  if (!data?.reason) return null;
  return {
    reason: data.reason,
    applicationStatus: data.application_status || "",
    applicationDate: data.application_date || "",
    applicationDateIsApproximate: data.application_date_is_approximate ?? undefined,
    expedited: data.expedited || "",
    expectedDate: data.expected_date || "",
    dateUnknown: Boolean(data.date_unknown),
  };
}

const countryAliases: Record<string, string> = {
  "united states of america": "US",
  "united states": "US",
  usa: "US",
  "u.s.a.": "US",
  "estados unidos": "US",
  "united kingdom": "GB",
  uk: "GB",
  spain: "ES",
  españa: "ES",
  italy: "IT",
  italia: "IT",
  india: "IN",
};

function countryCode(name: string | null | undefined, locale: string): string {
  const text = (name || "").trim();
  if (/^[A-Za-z]{2}$/.test(text)) return text.toUpperCase();
  const alias = countryAliases[text.toLocaleLowerCase(locale)];
  if (alias) return alias;
  if (!text) return "";
  const target = text.toLocaleLowerCase(locale);
  for (const language of [locale, "en"]) {
    const display = new Intl.DisplayNames([language], { type: "region" });
    for (const code of isoCountryCodes) {
      let label = "";
      try {
        label = display.of(code) || "";
      } catch {
        label = "";
      }
      if (label.toLocaleLowerCase(language) === target) return code;
    }
  }
  return "";
}

function sexCode(value: string | null | undefined): string {
  const text = (value || "").trim().toLowerCase();
  if (text === "m" || text.startsWith("male")) return "M";
  if (text === "f" || text.startsWith("female")) return "F";
  if (text === "x" || text.startsWith("unspecified")) return "X";
  return "";
}

function splitName(fullName: string | null | undefined): {
  given_names?: string;
  middle_names?: string;
  family_name?: string;
  second_family_name?: string;
} {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { given_names: parts[0] };
  if (parts.length === 2) return { given_names: parts[0], family_name: parts[1] };
  if (parts.length === 3) return { given_names: parts[0], middle_names: parts[1], family_name: parts[2] };
  return {
    given_names: parts[0],
    middle_names: parts.slice(1, -2).join(" "),
    family_name: parts[parts.length - 2],
    second_family_name: parts[parts.length - 1],
  };
}

function put(next: Answers, id: string, value: string) {
  if (value.trim()) next[id] = value.trim();
}

export function answersFromPassportScan(extracted: PassportExtract | null | undefined, locale: string): Answers {
  const next: Answers = {};
  if (!extracted) return next;
  const name = splitName(extracted.full_name);
  put(next, "pf.identity.given_names", name.given_names || "");
  put(next, "pf.identity.middle_names", name.middle_names || "");
  put(next, "pf.identity.family_primary", name.family_name || "");
  put(next, "pf.identity.family_second", name.second_family_name || "");
  put(next, "pf.identity.birth_date", extracted.date_of_birth || "");
  put(next, "pf.identity.birth_city", extracted.place_of_birth || "");
  put(next, "pf.identity.birth_country", countryCode(extracted.country_of_birth, locale));
  put(next, "pf.identity.nationalities", countryCode(extracted.nationality, locale));
  put(next, "pf.identity.sex_as_documented", sexCode(extracted.sex));
  put(next, "pf.passport.number", (extracted.passport_number || "").toUpperCase().replace(/[^A-Z0-9]/g, ""));
  put(next, "pf.passport.issuing_country", countryCode(extracted.issuing_country, locale));
  put(next, "pf.passport.expiry_date", extracted.expiry_date || "");
  return next;
}

export function passportDetailsPayload(answers: Answers, fileName: string, storageKey: string) {
  const nationalities = scalar(answers["pf.identity.nationalities"]).split(",").map((code) => code.trim()).filter(Boolean);
  return {
    given_names: scalar(answers["pf.identity.given_names"]).trim(),
    middle_names: scalar(answers["pf.identity.middle_names"]).trim() || null,
    family_name: scalar(answers["pf.identity.family_primary"]).trim(),
    second_family_name: scalar(answers["pf.identity.family_second"]).trim() || null,
    birth_date: scalar(answers["pf.identity.birth_date"]).trim(),
    birth_city: scalar(answers["pf.identity.birth_city"]).trim() || null,
    birth_country: scalar(answers["pf.identity.birth_country"]).trim() || null,
    nationalities,
    sex: scalar(answers["pf.identity.sex_as_documented"]).trim() || null,
    passport_number: scalar(answers["pf.passport.number"]).trim(),
    issuing_country: scalar(answers["pf.passport.issuing_country"]).trim(),
    issue_date: scalar(answers["pf.passport.issue_date"]).trim() || null,
    expiry_date: scalar(answers["pf.passport.expiry_date"]).trim(),
    file_name: fileName || null,
    storage_key: storageKey || null,
  };
}

export function answersFromSavedPassport(data: SavedPassport | null | undefined): Answers {
  const next: Answers = {};
  if (!data) return next;
  put(next, "pf.identity.given_names", data.given_names || "");
  put(next, "pf.identity.middle_names", data.middle_names || "");
  put(next, "pf.identity.family_primary", data.family_name || "");
  put(next, "pf.identity.family_second", data.second_family_name || "");
  put(next, "pf.identity.birth_date", data.birth_date || "");
  put(next, "pf.identity.birth_city", data.birth_city || "");
  put(next, "pf.identity.birth_country", data.birth_country || "");
  put(next, "pf.identity.nationalities", (data.nationalities || []).join(","));
  put(next, "pf.identity.sex_as_documented", data.sex || "");
  put(next, "pf.passport.number", data.passport_number || "");
  put(next, "pf.passport.issuing_country", data.issuing_country || "");
  put(next, "pf.passport.issue_date", data.issue_date || "");
  put(next, "pf.passport.expiry_date", data.expiry_date || "");
  return next;
}
